import { apiClient } from './apiClient';

export interface Detection {
  _id: string;
  type: 'abnormal_behavior' | 'fire_smoke' | 'fall_detection' | 'vehicle_collision';
  severity: 'critical' | 'high' | 'medium' | 'low';
  status: 'pending' | 'acknowledged' | 'resolved' | 'false_positive';
  location: string;
  confidence: number;
  description: string;
  timestamp: string;
  mediaUrl?: string;
  cameraId: {
    name: string;
  };
}

export interface AIStats {
  summary: {
    totalDetections: number;
    activeAlerts: number;
    resolvedToday: number;
    falsePositives: number;
    avgResponseTime: number;
  };
  byType: Record<string, { count: number; percentage: number }>;
  bySeverity: Record<string, { count: number; percentage: number }>;
  byCamera: Array<{
    cameraId: string;
    name: string;
    count: number;
    uptime: number;
  }>;
  timeline: Array<{
    time: string;
    count: number;
  }>;
  performance: {
    avgProcessingTime: number;
    successRate: number;
    errorRate: number;
  };
}

export interface Camera {
  _id: string;
  name: string;
  location: {
    zone: string;
    description: string;
    coordinates?: {
      lat: number;
      lng: number;
    };
  };
  status: 'online' | 'offline' | 'maintenance' | 'error';
  enabled: boolean;
  specs: {
    resolution: string;
    fps: number;
    fieldOfView: number;
    nightVision: boolean;
    audioCapture: boolean;
  };
  aiConfig: {
    enabledDetections: string[];
    thresholds: Record<string, any>;
  };
  network: {
    ipAddress: string;
    streamUrl: string;
  };
}

export interface LiveAlert {
  _id: string;
  type: string;
  severity: string;
  location: string;
  timestamp: string;
  description: string;
  cameraName: string;
  confidence: number;
}

export interface EngineStatus {
  isRunning: boolean;
  uptime: number;
  processedFrames: number;
  activeDetections: number;
  performance: {
    fps: number;
    cpuUsage: number;
    memoryUsage: number;
  };
}

class AIService {
  // 獲取檢測記錄
  async getDetections(params?: {
    type?: string;
    severity?: string;
    status?: string;
    page?: number;
    limit?: number;
  }): Promise<{
    detections: Detection[];
    pagination: {
      currentPage: number;
      totalPages: number;
      totalItems: number;
      itemsPerPage: number;
    };
  }> {
    const response = await apiClient.get('/ai/detections', { params });
    return response.data;
  }

  // 獲取單個檢測記錄
  async getDetectionById(id: string): Promise<Detection> {
    const response = await apiClient.get(`/ai/detections/${id}`);
    return response.data;
  }

  // 確認檢測
  async acknowledgeDetection(id: string): Promise<void> {
    await apiClient.put(`/ai/detections/${id}/acknowledge`);
  }

  // 標記為誤報
  async markAsFalsePositive(id: string): Promise<void> {
    await apiClient.put(`/ai/detections/${id}/false-positive`);
  }

  // 獲取統計數據
  async getStats(period?: string): Promise<AIStats> {
    const response = await apiClient.get('/ai/stats', { 
      params: { period } 
    });
    return response.data;
  }

  // 獲取實時警報
  async getLiveAlerts(): Promise<LiveAlert[]> {
    const response = await apiClient.get('/ai/alerts/live');
    return response.data.data || [];
  }

  // 獲取攝像頭配置
  async getCameras(): Promise<Camera[]> {
    const response = await apiClient.get('/ai/cameras');
    return response.data;
  }

  // 更新攝像頭配置
  async updateCamera(cameraId: string, config: Partial<Camera>): Promise<void> {
    await apiClient.put(`/ai/cameras/${cameraId}`, config);
  }

  // 獲取引擎狀態
  async getEngineStatus(): Promise<EngineStatus> {
    const response = await apiClient.get('/ai/engine/status');
    return response.data;
  }

  // 啟動AI引擎
  async startEngine(): Promise<void> {
    await apiClient.post('/ai/engine/start');
  }

  // 停止AI引擎
  async stopEngine(): Promise<void> {
    await apiClient.post('/ai/engine/stop');
  }

  // 切換引擎狀態
  async toggleEngine(): Promise<void> {
    await apiClient.post('/ai/engine/toggle');
  }
}

export const aiService = new AIService();